/**
 * Dashboard — page shell. Owns the header (greeting + primary CTAs),
 * the sticky tab strip, and page-level data fetches; delegates the actual
 * tab bodies to /components/dashboard/{TodayTab,ProgressTab,ToolsTab}.
 *
 * Prior shape (pre-Feb 2026): a single 1280-LOC monolith with 9 inline
 * widget components and the three tab JSX bodies all in this file.
 * Split out for testability and so each tab can mount lazily.
 */
import { useState, useEffect, lazy, Suspense } from 'react';
import axios from 'axios';
import {
  Brain, Mic, GraduationCap, BookOpen, Lightbulb, Clock,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import FocusTimer from '../components/FocusTimer';
import OnboardingFlow from '../components/OnboardingFlow';
import PageSkeleton from '../components/PageSkeleton';
import MinimizedFocusWidget from '../components/MinimizedFocusWidget';
import DashboardWalkthrough from '../components/DashboardWalkthrough';
import { UpgradeModal } from '../components/UpgradeModal';
import Tip from '../components/Tip';
import { SubscriptionBanner } from '../components/dashboard/widgets';
import TodayTab from '../components/dashboard/TodayTab';
// iter196 — Progress + Tools tabs are heavy (analytics charts, dozens
// of widget cards). Lazy-load them so the Today first-paint is sub-300ms.
// Both tabs only render when their tab is active; default tab is Today
// so for the vast majority of dashboard mounts neither chunk loads.
const ProgressTab = lazy(() => import('../components/dashboard/ProgressTab'));
const ToolsTab = lazy(() => import('../components/dashboard/ToolsTab'));
import StreakHero from '../components/dashboard/StreakHero';
import UsageBadge from '../components/UsageBadge';
import { UniversityRankChip, ActivePactChip } from '../components/dashboard/HeaderSocialChips';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showTimer, setShowTimer] = useState(false);

  // Hot-path widgets migrated to TanStack Query (Feb 2026 quick win).
  // Cache is shared across navigation, so flipping between Dashboard tabs
  // and the Lectures page re-uses the same response. 60s staleTime keeps
  // the data feeling live without re-fetching on every tab click.
  const { data: lecturesData } = useQuery({
    queryKey: ['lectures', 'list'],
    queryFn: async () => (await axios.get(`${API}/lectures`)).data,
    staleTime: 60_000,
  });
  const { data: examsData } = useQuery({
    queryKey: ['exams', 'list'],
    queryFn: async () => (await axios.get(`${API}/exams`)).data,
    staleTime: 60_000,
  });
  const recentLectures = (lecturesData || []).slice(0, 5);
  const upcomingExams = (examsData || []).filter((e) => !e.completed).slice(0, 5);

  // UX simplification (Feb 2026) — collapse the 23-widget firehose into
  // 3 tabs. Tab is URL-synced (?tab=today|progress|tools) AND persisted
  // in localStorage so power users land on their last tab on next visit.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const fromUrl = searchParams.get('tab');
    if (fromUrl && ['today', 'progress', 'tools'].includes(fromUrl)) return fromUrl;
    try {
      const stored = localStorage.getItem('isp_dashboard_tab');
      if (stored && ['today', 'progress', 'tools'].includes(stored)) return stored;
    } catch {/* localStorage unavailable */}
    return 'today';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);

  const selectTab = (tab) => {
    setActiveTab(tab);
    try { localStorage.setItem('isp_dashboard_tab', tab); } catch {/* ignore */}
    // Keep URL in sync without polluting history with one entry per click
    const next = new URLSearchParams(searchParams);
    if (tab === 'today') next.delete('tab'); else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // Wrapped card is only relevant in the Dec–Jan window. Hide it the rest
  // of the year so the Today tab doesn't carry irrelevant noise in April.
  const isWrappedSeason = (() => {
    const m = new Date().getMonth(); // 0-indexed
    return m === 11 || m === 0; // December or January
  })();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Check if activation flow should show
  useEffect(() => {
    if (user && user.activation_flow_complete !== true) {
      setShowOnboarding(true);
    }
  }, [user]);

  // Listen for explicit "dismiss the walkthrough" events (e.g. when a
  // user clicks the WrappedCard whose backdrop intercepted by the
  // walkthrough overlay). Lets us layer in additional entry points
  // without pushing each one through an explicit "Skip" CTA.
  useEffect(() => {
    const onDismiss = () => setShowOnboarding(false);
    window.addEventListener('istudypro:onboarding:dismiss', onDismiss);
    return () => window.removeEventListener('istudypro:onboarding:dismiss', onDismiss);
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Lectures + exams + srs/stats come from useQuery (cache-shared) —
      // we fetch the rest here so the legacy `loading` flag stays in sync.
      const [statsRes, subRes, recsRes, streakRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/subscription/status`),
        axios.get(`${API}/recommendations/dashboard`).catch(() => ({ data: { recommendations: [] } })),
        axios.get(`${API}/gamification/streak`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setSubscription(subRes.data);
      setRecommendations(recsRes.data?.recommendations || []);
      setStreakData(streakRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="font-body f500-page" data-testid="dashboard-loading">
        <PageSkeleton variant="dashboard" />
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'Scholar';

  const statCards = [
    { label: 'Lectures', value: stats?.total_lectures || 0, icon: GraduationCap, gradient: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50', text: 'text-blue-600', testid: 'stat-total-lectures' },
    { label: 'Subjects', value: stats?.total_subjects || 0, icon: BookOpen, gradient: 'from-violet-500 to-purple-500', bg: 'bg-violet-50', text: 'text-violet-600', testid: 'stat-subjects' },
    { label: 'Flashcards', value: stats?.learning_bytes_count || 0, icon: Lightbulb, gradient: 'from-amber-500 to-orange-500', bg: 'bg-amber-50', text: 'text-amber-600', testid: 'stat-bytes' },
    { label: 'Study Hours', value: stats?.total_study_hours || 0, icon: Clock, gradient: 'from-rose-500 to-pink-500', bg: 'bg-rose-50', text: 'text-rose-600', testid: 'stat-hours' },
  ];

  return (
    <div className="font-body f500-page" data-testid="dashboard">
      {/* Onboarding */}
      {showOnboarding && <OnboardingFlow onComplete={() => setShowOnboarding(false)} />}

      {/* Streak Hero — full-width retention surface above the greeting.
          Self-hides on Day 0 with no streak content; switches to an
          urgency state ("Save your streak — 3h left") when the user's
          streak is alive but today not yet counted. Feb 2026 retention. */}
      <StreakHero />

      {/* Page eyebrow + greeting. iter184: greeting now uses the serif
          accent font for an emotional, Wave-style welcome moment. The
          rest of the headings (h2/h3) stay on SF Pro Display — the
          serif is reserved for *this one line* so it stays special. */}
      <div className="border-b border-[var(--f500-line)] pb-6 mb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="f500-eyebrow mb-2">— Dashboard / {format(new Date(), 'EEE, dd MMM')}</p>
            <h1 className="font-serif-accent text-4xl sm:text-5xl text-[var(--isp-heading)]" data-testid="dashboard-greeting">
              {(() => {
                const h = new Date().getHours();
                const greet = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
                return `${greet}, ${firstName}.`;
              })()}
            </h1>
            {/* iter195 social-pressure chips — auto-hide when the data
                source is null (no institution / no active pact). */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <UniversityRankChip />
              <ActivePactChip />
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="flex flex-col items-end gap-2"
          >
            <div className="flex gap-2">
              <Link to="/study-buddy">
                <Tip text="Chat with AI about your study notes and get help">
                  <button className="f500-btn f500-btn-secondary" data-testid="study-buddy-btn">
                    <Brain className="w-4 h-4" aria-hidden /> Study Buddy
                  </button>
                </Tip>
              </Link>
              <Link to="/lectures">
                <Tip text="Start recording a new lecture with AI transcription">
                  <button className="f500-btn f500-btn-primary" data-testid="new-lecture-button">
                    <Mic className="w-4 h-4" aria-hidden /> Record Lecture
                  </button>
                </Tip>
              </Link>
            </div>
            {/* iter193 predictive paywall — surfaces "1 of 5 left" or
                "Upgrade" hint inline with the Record CTA so free-tier
                friction is visible *before* the click, not as a 402. */}
            <UsageBadge resource="lectures" />
          </motion.div>
        </div>
      </div>

      {/* Subscription Banner — visible across all tabs (high-priority signal) */}
      <SubscriptionBanner subscription={subscription} user={user} onUpgrade={() => setShowUpgrade(true)} />

      {/* ─── Tab strip ──────────────────────────────────────────────
          Default "Today" — what a returning daily user needs in <5s.
          "Progress" — analytics deep-dive. "Tools" — utilities & quick links.
          Sticky on scroll so users always know where they are.
          iter186: pulled UP above HabitLoopCard so the tab strip sits
          immediately under the greeting. HabitLoopCard now renders inside
          the Today panel body (after the onboarding row). */}
      <div
        className="sticky top-0 z-20 bg-[var(--isp-page)]/95 backdrop-blur-md -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 border-b border-[var(--f500-line)] mb-6"
        data-testid="dashboard-tabs"
      >
        <div className="flex gap-0 overflow-x-auto no-scrollbar" role="tablist" aria-label="Dashboard sections">
          {[
            { id: 'today', label: 'Today', testid: 'tab-today' },
            { id: 'progress', label: 'Progress', testid: 'tab-progress' },
            { id: 'tools', label: 'Tools', testid: 'tab-tools' },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                aria-controls={`dashboard-panel-${tab.id}`}
                onClick={() => selectTab(tab.id)}
                className={`relative px-5 py-3 text-sm font-semibold transition-colors whitespace-nowrap ${
                  isActive
                    ? 'text-[var(--isp-heading)]'
                    : 'text-[var(--isp-muted)] hover:text-[var(--isp-heading)]'
                }`}
                data-testid={tab.testid}
                style={{ fontFamily: 'var(--sf-text, inherit)' }}
              >
                {tab.label}
                {isActive && (
                  <motion.span
                    layoutId="dashboard-tab-indicator"
                    className="absolute left-2 right-2 -bottom-px h-0.5 bg-[#002FA7]"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ TAB BODIES — only the active tab mounts, so heatmap/charts in
          Progress don't run while a daily user sits on Today. Progress + Tools
          are lazy-loaded (React.lazy) so their bundles are only fetched on
          the first time the user opens that tab. Suspense shows a quiet
          skeleton during the tiny chunk-fetch interval. ═══ */}
      {activeTab === 'today' && (
        <TodayTab
          recentLectures={recentLectures}
          upcomingExams={upcomingExams}
          isWrappedSeason={isWrappedSeason}
        />
      )}
      {activeTab === 'progress' && (
        <Suspense fallback={<PageSkeleton variant="dashboard" />}>
          <ProgressTab
            statCards={statCards}
            streakData={streakData}
            upcomingExams={upcomingExams}
          />
        </Suspense>
      )}
      {activeTab === 'tools' && (
        <Suspense fallback={<PageSkeleton variant="dashboard" />}>
          <ToolsTab
            subscription={subscription}
            recommendations={recommendations}
            isWrappedSeason={isWrappedSeason}
            onUpgrade={() => setShowUpgrade(true)}
          />
        </Suspense>
      )}

      {/* Focus Timer Modal */}
      <FocusTimer isOpen={showTimer} onClose={() => setShowTimer(false)} />

      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />
      <MinimizedFocusWidget />
      {/* Four-step usage walkthrough — bottom-right, dismissible, gated
          by localStorage so it only appears for first-time users. */}
      <DashboardWalkthrough />
    </div>
  );
};

export default Dashboard;
